import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import i18n from 'i18next';
import { ModifyUserInfoProps, SaveFeedbackProps } from 'types';
import { create } from 'zustand';
import { shallow } from 'zustand/shallow';
import useSystemStore from '../SystemStore';
import useUserStore from '../UserStore';

const JSONbigNative = require('json-bigint')({ useNativeBigInt: true });

interface APIStates {
	fetching: number;
}

interface APIActions {
	actions: {
		makeRequest: (
			config: AxiosRequestConfig,
			reqAuth?: 'app' | 'user' | 'auto',
			throwMsg?: true | false,
			showBackdrop?: true | false
		) => Promise<any>;
		cancelRequest: () => void;
	};
	api: {
		getBanner: () => Promise<any>;
		getScanHistory: (page: number) => Promise<any>;
		getPurchaseHistory: (page: number) => Promise<any>;
		getHistoryDetail: (id: string) => Promise<any>;
		importOwnershipCard: (content: string) => Promise<any>;
		getSessionToken: () => Promise<any>;
		refreshToken: (token: string) => Promise<any>;
		getVerificationCode: (mail: string) => Promise<any>;
		getValidationCode: () => Promise<any>;
		register: (
			user: string,
			mail: string,
			pwd: string,
			captcha: string,
			tkId: string
		) => Promise<any>;
		login: (username: string, password: string) => Promise<any>;
		googleLogin: (authCode: string) => Promise<any>;
		WeChatLogin: (authCode: string) => Promise<any>;
		logout: () => Promise<any>;
		destroy: (captcha: string) => Promise<any>;
		resetPassword: (
			mail: string,
			pwd: string,
			captcha: string,
			tkId: string
		) => Promise<any>;
		changePassword: (oldPwd: string, newPwd: string) => Promise<any>;
		getUserInfo: () => Promise<any>;
		uploadImage: (image: File) => Promise<any>;
		modifyUserInfo: (info: ModifyUserInfoProps) => Promise<any>;
		genOwnershipCard: (
			signature: string,
			scantime: number,
			uaid: string
		) => Promise<any>;
		authenticateUAID: (
			uaid: string,
			signature: any,
			authenticationId: any,
			challengeCode: any,
			response: any,
			webRepeatScan: boolean
		) => Promise<any>;
		getUAIDInfo: (uaid: string) => Promise<any>;
		sendGift: (verifyId: string) => Promise<any>;
		saveFeedback: (data: SaveFeedbackProps) => Promise<void>;
		getFeedbackHistory: (
			uaid: string,
			page: number,
			title?: string
		) => Promise<any>;
		getFeedbackDetail: (id: string) => Promise<any>;
		getPointInfo: () => Promise<any>;
		getStoreGroupList: () => Promise<any>;
		getRewardCategoryList: () => Promise<any>;
		getRewardList: (
			page: number,
			groupId?: string,
			categoryId?: string
		) => Promise<any>;
		getRewardDetail: (id: string) => Promise<any>;
		getPointHistory: (page: number) => Promise<any>;
		getRedeemHistory: (page: number) => Promise<any>;
		redeemRewards: (id: string, num: number) => Promise<any>;
		obtainWXJSConfig: () => Promise<any>;
	};
}

const controller = new AbortController();

const axiosInstance = axios.create({
	baseURL: process.env.REACT_APP_DOMAIN_NAME,
	timeout: 30000,
	signal: controller.signal,
	headers: {
		'content-type': 'application/json; charset=utf-8',
	},
});

const useAPIStore = create<APIStates & APIActions>((set, get) => ({
	fetching: 0,
	actions: {
		makeRequest: async (
			config,
			reqAuth,
			throwMsg = true,
			showBackdrop = true
		) => {
			const count = showBackdrop ? 1 : 0;
			try {
				set((state) => ({ fetching: state.fetching + count }));
				let tokenString: string | undefined;
				switch (reqAuth) {
					case 'app':
					case 'user':
						const token =
							useUserStore.getState()[`${reqAuth}Token`];
						token &&
							(tokenString = `${token.tokenType} ${token.sessionToken}`);
						break;
					case 'auto':
						tokenString = useUserStore
							.getState()
							.actions.acquireToken();
						break;
				}
				tokenString &&
					(config.headers = {
						...config.headers,
						Authorization: tokenString,
					});
				const response: AxiosResponse = await axiosInstance(config);
				if (throwMsg && response.data && response.data.code !== '0') {
					return Promise.reject(
						Error(response.data.message[i18n.resolvedLanguage])
					);
				}
				return response.data;
			} catch (error) {
				if (axios.isAxiosError(error))
					return Promise.reject(Error(error.message));
				return Promise.reject(Error('Unknown error'));
			} finally {
				set((state) => ({ fetching: state.fetching - count }));
			}
		},
		cancelRequest: () => {
			controller.abort();
		},
	},
	api: {
		getBanner: async () => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_BANNER,
				method: 'POST',
			};
			return await get().actions.makeRequest(config, 'app', true, false);
		},
		getScanHistory: async (page) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_SCAN_HISTORY,
				method: 'POST',
				data: {
					pageNo: page,
					pageSize: 10,
					type: 3,
				},
				transformResponse: (data) => JSONbigNative.parse(data),
			};
			return await get().actions.makeRequest(config, 'user', true, false);
		},
		getPurchaseHistory: async (page) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_PURCHASE_HISTORY,
				method: 'POST',
				data: {
					pageNo: page,
					pageSize: 10,
				},
				transformResponse: (data) => JSONbigNative.parse(data),
			};
			return await get().actions.makeRequest(config, 'user', true, false);
		},
		getHistoryDetail: async (id) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_HISTORY_DETAIL,
				method: 'POST',
				data: {
					id: id,
					responseTemplate: '1,2,3,4',
				},
			};
			return await get().actions.makeRequest(config, 'user', true, false);
		},
		importOwnershipCard: async (content) => {
			const JSONContent = JSON.parse(content);
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_IMPORT_OWNERSHIP_CARD,
				method: 'POST',
				data: {
					uaid: JSONContent['huaid'],
					signature: JSONContent['signature'],
				},
			};
			return await get().actions.makeRequest(config, 'user', true);
		},
		getSessionToken: async () => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_GET_SESSION_TOKEN,
				method: 'POST',
				data: {
					appId: process.env.REACT_APP_APPID,
					apiKey: process.env.REACT_APP_APIKEY,
				},
			};
			return await get().actions.makeRequest(config);
		},
		refreshToken: async (token) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_REFRESH_SESSION_TOKEN,
				method: 'POST',
				data: {
					refreshToken: token,
				},
			};
			return await get().actions.makeRequest(config);
		},
		getVerificationCode: async (mail) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_GET_VERIFICATION_CODE,
				method: 'POST',
				data: {
					mail: mail,
				},
			};
			return await get().actions.makeRequest(config, 'app');
		},
		getValidationCode: async () => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_GET_VALIDATION_CODE,
				method: 'POST',
			};
			return await get().actions.makeRequest(config, 'user', true, false);
		},
		register: async (user, mail, pwd, captcha, tkId) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_REGISTER,
				method: 'POST',
				data: {
					loginName: user,
					mail: mail,
					pwd: pwd,
					captcha: captcha,
					tkId: tkId,
				},
			};
			return await get().actions.makeRequest(config, 'app');
		},
		login: async (username, password) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_LOGIN,
				method: 'POST',
				data: {
					appId: process.env.REACT_APP_APPID,
					apiKey: process.env.REACT_APP_APIKEY,
					username: username,
					password: password,
				},
			};
			return await get().actions.makeRequest(config);
		},
		googleLogin: async (authCode) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_GOOGLE_LOGIN,
				method: 'POST',
				params: {
					code: authCode,
					openApiId: process.env.REACT_APP_APPID,
					openApiKey: process.env.REACT_APP_APIKEY,
				},
			};
			return await get().actions.makeRequest(config);
		},
		WeChatLogin: async (authCode) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_WECHAT_LOGIN,
				method: 'POST',
				params: {
					code: authCode,
					openApi: `${process.env.REACT_APP_APPID},${process.env.REACT_APP_APIKEY}`,
				},
			};
			return await get().actions.makeRequest(config, undefined, false);
		},
		logout: async () => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_LOGOUT,
				method: 'POST',
			};
			return await get().actions.makeRequest(config, 'user');
		},
		destroy: async (captcha) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_DESTROY,
				method: 'POST',
				data: {
					captcha: captcha,
				},
			};
			return await get().actions.makeRequest(config, 'user');
		},
		resetPassword: async (mail, pwd, captcha, tkId) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_RESET_PASSWORD,
				method: 'POST',
				data: {
					mail: mail,
					pwd: pwd,
					captcha: captcha,
					tkId: tkId,
				},
			};
			return await get().actions.makeRequest(config, 'app');
		},
		changePassword: async (oldPwd, newPwd) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_CHANGE_PASSWORD,
				method: 'POST',
				data: {
					oldPwd: oldPwd,
					newPwd: newPwd,
				},
			};
			return await get().actions.makeRequest(config, 'user');
		},
		getUserInfo: async () => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_GET_USER_INFO,
				method: 'POST',
			};
			return await get().actions.makeRequest(config, 'user');
		},
		uploadImage: async (image) => {
			let formData = new FormData();
			formData.append('file', image);
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_UPLOAD_IMAGE,
				method: 'POST',
				headers: { 'content-type': 'multipart/form-data' },
				data: formData,
			};
			return await get().actions.makeRequest(config, 'user');
		},
		modifyUserInfo: async (info) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_MODIFY_USER_INFO,
				method: 'POST',
				data: info,
			};
			return await get().actions.makeRequest(config, 'user');
		},
		genOwnershipCard: async (signature, scantime, uaid) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_GEN_OWNERSHIPCARD,
				method: 'POST',
				headers: {
					locales: i18n.resolvedLanguage,
				},
				data: {
					signature: signature,
					scantime: scantime,
					uaid: uaid,
				},
			};
			return await get().actions.makeRequest(config, 'app');
		},
		//authentication code and authorization code need authtnticationId, challengeCode and response help //
		//to conduct the authentication //
		authenticateUAID: async (
			uaid,
			signature,
			authenticationId,
			challengeCode,
			response,
			webRepeatScan
		) => {
			//
			const gps = useSystemStore.getState().gps;
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_OPENAPI_UAID_AUTH_API,
				method: 'POST',
				data: {
					uaid: uaid,
					signature: signature, //
					application: {
						appName: 'webscan',
						version: '2.0',
						type: useSystemStore.getState().actions.isWeChat()
							? '2' //	Temp solution for abnormal behaviour of type 3
							: '2',
					}, //
					options: {
						responseTemplate: '1,2,3,4',
					}, //
					challengeResponse: {
						//
						authenticationId: authenticationId, //
						challengeCode: challengeCode, //
						response: response, //
					},
					webRepeatScan: webRepeatScan,
					location: gps,
				},
			};
			return await get().actions.makeRequest(
				config,
				'auto',
				false,
				false
			); //
		},
		getUAIDInfo: async (uaid) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_OPENAPI_UAID_INFO_API,
				method: 'POST',
				data: {
					uaid: uaid,
					options: {
						responseTemplate: '1,2,3,4',
					},
				},
			};
			return await get().actions.makeRequest(config, 'app', false);
		},
		sendGift: async (verifyId) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_SEND_GIFT,
				method: 'POST',
				data: {
					giftType: '2',
					verifyId: verifyId,
				},
			};
			return await get().actions.makeRequest(config, 'user', true, false);
		},
		saveFeedback: async (data) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_SAVE_FEEDBACK,
				method: 'POST',
				data: data,
			};
			return await get().actions.makeRequest(config, 'user');
		},
		getFeedbackHistory: async (uaid, page, title) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_FEEDBACK_HISTORY,
				method: 'POST',
				data: {
					uaid: uaid,
					title: title,
					pageNo: page,
					pageSize: 10,
				},
				transformResponse: (data) => JSONbigNative.parse(data),
			};
			return await get().actions.makeRequest(config, 'user', true, false);
		},
		getFeedbackDetail: async (id) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_FEEDBACK_DETAIL,
				method: 'POST',
				data: {
					id: id,
				},
			};
			return await get().actions.makeRequest(config, 'user', true, false);
		},
		getPointInfo: async () => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_GET_POINT_INFO,
				method: 'POST',
				data: {},
			};
			return await get().actions.makeRequest(config, 'user', true, false);
		},
		getStoreGroupList: async () => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_GET_STORE_GROUP_LIST,
				method: 'POST',
				data: {},
				transformResponse: (data) => JSONbigNative.parse(data),
			};
			return await get().actions.makeRequest(config, 'user', true, false);
		},
		getRewardCategoryList: async () => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_GET_REWARD_CAT_LIST,
				method: 'POST',
				data: {},
				transformResponse: (data) => JSONbigNative.parse(data),
			};
			return await get().actions.makeRequest(config, 'user', true, false);
		},
		getRewardList: async (page, groupId, categoryId) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_GET_REWARD_LIST,
				method: 'POST',
				data: {
					status: 1,
					groupId: groupId,
					categoryId: categoryId,
					pageNo: page,
					pageSize: 10,
				},
				transformResponse: (data) => JSONbigNative.parse(data),
			};
			return await get().actions.makeRequest(config, 'user', true, false);
		},
		getRewardDetail: async (id) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_GET_REWARD_DETAIL,
				method: 'POST',
				data: {
					id: id,
				},
			};
			return await get().actions.makeRequest(config, 'user', true, false);
		},
		getPointHistory: async (page) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_GET_POINT_HISTORY,
				method: 'POST',
				data: {
					pageNo: page,
					pageSize: 10,
				},
				transformResponse: (data) => JSONbigNative.parse(data),
			};
			return await get().actions.makeRequest(config, 'user', true, false);
		},
		getRedeemHistory: async (page) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_GET_REDEEM_HISTORY,
				method: 'POST',
				data: {
					pageNo: page,
					pageSize: 10,
				},
				transformResponse: (data) => JSONbigNative.parse(data),
			};
			return await get().actions.makeRequest(config, 'user', true, false);
		},
		redeemRewards: async (id, num) => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_API_REDEEM,
				method: 'POST',
				data: {
					id: id,
					num: num,
				},
			};
			return await get().actions.makeRequest(config, 'user');
		},
		obtainWXJSConfig: async () => {
			const config: AxiosRequestConfig = {
				url: process.env.REACT_APP_WECHAT_JSCONFIG,
				method: 'POST',
				data: {
					url: window.location.href,
					jsApiList: ['scanQRCode', 'closeWindow'],
					isDebug: false,
				},
			};
			return await get().actions.makeRequest(config, 'user');
		},
	},
}));

export const useAPIStates = () =>
	useAPIStore(
		(state) => ({
			fetching: state.fetching,
		}),
		shallow
	);
export const useAPIActions = () => useAPIStore((state) => state.actions);
export const useAPI = () => useAPIStore((state) => state.api);

export default useAPIStore;
